/**
 * Angemeldete Person + Rollenprüfung fuers Frontend.
 *
 * Wichtig: `can(...)` blendet Bedienelemente aus, es SICHERT nichts ab. Die
 * verbindliche Prüfung passiert im Server (`plugins/rbac.ts`). Beides muss
 * uebereinstimmen — deshalb steht die Rollenlogik hier kompakt an einer Stelle und
 * nicht verstreut in `v-if`s.
 */

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Role, SessionUserDto, UserDto } from '@pmpro/shared';
import { ApiRequestError, api, setCsrfToken } from '../api/client';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserDto | null>(null);
  /** `false`, solange nicht geklaert ist, ob eine gültige Sitzung besteht. */
  const ready = ref(false);

  const isAuthenticated = computed(() => user.value !== null);
  const role = computed<Role | null>(() => user.value?.role ?? null);

  function apply(session: SessionUserDto): void {
    user.value = session.user;
    setCsrfToken(session.csrfToken);
  }

  function clear(): void {
    user.value = null;
    setCsrfToken('');
  }

  /** Beim Seitenaufruf einmal fragen, ob das Sitzungs-Cookie noch gilt. */
  async function restore(): Promise<void> {
    try {
      apply(await api.get<SessionUserDto>('/api/auth/me'));
    } catch (error) {
      if (!(error instanceof ApiRequestError && error.isUnauthorized)) {
        console.warn('Sitzung konnte nicht geprüft werden', error);
      }
      clear();
    } finally {
      ready.value = true;
    }
  }

  async function login(email: string, password: string): Promise<void> {
    apply(await api.post<SessionUserDto>('/api/auth/login', { email, password }));
    ready.value = true;
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/api/auth/logout');
    } finally {
      clear();
    }
  }

  /**
   * Eigenes Passwort ändern. Die eigene Sitzung bleibt bestehen (der Server beendet
   * nur die anderen), deshalb ist hier nichts am Zustand nachzuziehen — ein Fehler
   * wird geworfen und im Formular angezeigt.
   */
  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/api/auth/password', { currentPassword, newPassword });
  }

  /** Hat die angemeldete Person eine dieser Rollen? */
  function hasRole(...roles: Role[]): boolean {
    return role.value !== null && roles.includes(role.value);
  }

  /** Darf Stammdaten pflegen — Produkte, Features, Bewertungen, Roadmap. */
  const canEdit = computed(() => hasRole('admin', 'product_manager'));
  /** Darf Benutzer und Systemeinstellungen verwalten. */
  const canAdminister = computed(() => hasRole('admin'));

  return {
    user,
    ready,
    isAuthenticated,
    role,
    canEdit,
    canAdminister,
    hasRole,
    restore,
    login,
    logout,
    changePassword,
    clear
  };
});
